import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Check, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, gbp, errMsg, STATUS_LABELS } from "@/lib/api";

const STEPS = ["paid", "shopping", "shipped", "delivered"];

const Timeline = ({ status }) => {
  const idx = STEPS.indexOf(status);
  return (
    <ol className="grid grid-cols-4 gap-2 mt-8" data-testid="order-timeline">
      {STEPS.map((s, i) => (
        <li key={s} className="text-center">
          <div className={`mx-auto w-9 h-9 rounded-full grid place-items-center ${i <= idx ? "bg-[#1B3626] text-white" : "bg-[#EDE4D3] text-slate-400"}`}><Check className="w-4 h-4" /></div>
          <p className={`mt-2 text-xs ${i <= idx ? "font-semibold" : "text-slate-400"}`}>{STATUS_LABELS[s]}</p>
        </li>
      ))}
    </ol>
  );
};

export default function TrackOrder() {
  const [params] = useSearchParams();
  const [num, setNum] = useState(params.get("order") || "");
  const [email, setEmail] = useState("");
  const [order, setOrder] = useState(null);

  const lookup = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.get("/orders/track", { params: { order_number: num, email } });
      setOrder(data);
    } catch (e2) {
      setOrder(null);
      toast.error(errMsg(e2));
    }
  };

  const cancelSub = async () => {
    if (!window.confirm("Cancel your monthly box? You won't be charged again.")) return;
    try {
      await api.post("/subscriptions/cancel", { order_number: order.order_number, email });
      setOrder({ ...order, subscription_status: "cancelled" });
      toast.success("Your monthly box has been cancelled");
    } catch (e2) { toast.error(errMsg(e2)); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-16">
      <h1 className="font-serif text-4xl sm:text-5xl font-bold">Track your order</h1>
      <form onSubmit={lookup} className="mt-8 grid sm:grid-cols-[1fr_1fr_auto] gap-3" data-testid="track-form">
        <input required value={num} onChange={(e) => setNum(e.target.value)} placeholder="Order number e.g. AP-123456" className="rounded-xl border border-[#E2D7C3] bg-white px-4 py-3" data-testid="track-order-number-input" />
        <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email used at checkout" className="rounded-xl border border-[#E2D7C3] bg-white px-4 py-3" data-testid="track-email-input" />
        <button className="rounded-xl bg-[#8C2220] text-white px-6 py-3 font-semibold hover:bg-[#701A18]" data-testid="track-submit-button">Track</button>
      </form>
      {order && (
        <div className="mt-10 rounded-3xl bg-white border border-[#EDE4D3] p-6 sm:p-8 rise" data-testid="track-result">
          <div className="flex flex-wrap justify-between gap-2">
            <p className="font-mono font-bold text-[#8C2220]">{order.order_number}</p>
            <p className="text-sm font-semibold" data-testid="track-status">{STATUS_LABELS[order.status]}</p>
          </div>
          {order.status === "cancelled" ? (
            <p className="mt-6 rounded-xl bg-rose-50 text-rose-800 p-4 text-sm" data-testid="track-cancelled">Sorry, your items weren't available at our local stores. We've refunded {gbp(order.refunded_amount)} to your card.</p>
          ) : <Timeline status={order.status} />}
          {order.tracking_number && (
            <a href={`https://www.evri.com/track/parcel/${encodeURIComponent(order.tracking_number)}/details`} target="_blank" rel="noreferrer" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold underline" data-testid="evri-tracking-link">
              Evri tracking: {order.tracking_number} <ExternalLink className="w-4 h-4" />
            </a>
          )}
          {order.subscription_status && (
            <div className="mt-6 rounded-xl bg-[#1E2A44]/5 border border-[#1E2A44]/20 p-4 flex flex-wrap items-center justify-between gap-3" data-testid="track-subscription">
              <p className="text-sm"><b>Monthly box:</b> {order.subscription_status === "active" ? "active — posted every month at 10% off" : "cancelled"}</p>
              {order.subscription_status === "active" && <button onClick={cancelSub} className="rounded-full border border-[#1E2A44] px-4 py-2 text-sm font-semibold hover:bg-[#1E2A44] hover:text-white transition-colors" data-testid="cancel-subscription-button">Cancel monthly box</button>}
            </div>
          )}
          {order.gift_message && (
            <p className="mt-6 rounded-xl bg-[#F7F3EB] border-l-4 border-[#8C2220] p-4 font-serif italic" data-testid="track-gift-message">
              <span className="not-italic font-sans text-xs font-semibold text-[#8C2220] block mb-1">Your gift message</span>“{order.gift_message}”
            </p>
          )}
          <div className="mt-6 divide-y divide-[#EDE4D3] text-sm">
            {order.items.map((i, k) => (
              <div key={k} className="py-3 flex justify-between gap-3">
                <span>{i.name} × {i.qty}{i.contents && <small className="block text-xs text-slate-500">{i.contents.map((c) => c.name).join(" · ")}</small>}{order.status !== "cancelled" && i.qty_available < i.qty && <em className="ml-2 text-rose-700 not-italic">({i.qty - i.qty_available} out of stock – refunded)</em>}</span>
                <span>{gbp(i.price * i.qty)}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 text-sm space-y-1 border-t border-[#EDE4D3] pt-4">
            <div className="flex justify-between"><span>Delivery ({order.delivery.label})</span><span>{gbp(order.delivery_fee)}</span></div>
            <div className="flex justify-between font-bold"><span>Total paid</span><span>{gbp(order.total)}</span></div>
            {order.refunded_amount > 0 && <div className="flex justify-between text-emerald-700" data-testid="track-refunded"><span>Refunded</span><span>-{gbp(order.refunded_amount)}</span></div>}
          </div>
        </div>
      )}
    </div>
  );
}
